import { useState } from 'react'
import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import { Link } from 'react-router-dom'
import { visibleDocs, type DocEntry } from '@/content/docs/registry'
import { useFeatureStore } from '@/stores/featureStore'
import { apiClient } from '@/services/apiClient'

export function DocsIndexPage() {
  // Subscribe to `features` (replaced when the manifest loads) so the list
  // re-filters after load; `isEnabled` alone is a stable ref and would not re-render.
  useFeatureStore((s) => s.features)
  useFeatureStore((s) => s.isLoaded)
  const isEnabled = useFeatureStore((s) => s.isEnabled)
  // In-product help shows public + authenticated tiers (the user is logged in here).
  const entries = visibleDocs({ authenticated: true, isEnabled })

  const [pdfBusy, setPdfBusy] = useState(false)
  const [pdfError, setPdfError] = useState(false)

  // The PDF endpoint is auth-gated via the `Authorization: Bearer` header, but
  // the access token lives in memory (Zustand) — a plain <a href> navigation
  // sends only cookies, so it would 401. Fetch through apiClient (which attaches
  // the JWT and silently refreshes) and download from the returned blob.
  async function downloadUserGuide() {
    setPdfError(false)
    setPdfBusy(true)
    try {
      const res = await apiClient.fetch('/api/docs/user-guide.pdf')
      if (!res.ok) throw new Error(`PDF download failed (${res.status})`)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'ai-expert-user-guide.pdf'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch {
      setPdfError(true)
    } finally {
      setPdfBusy(false)
    }
  }

  // Group by section, preserving the registry's order-based sort.
  const sections = new Map<string, DocEntry[]>()
  for (const d of entries) {
    const list = sections.get(d.section) ?? []
    list.push(d)
    sections.set(d.section, list)
  }

  return (
    <PageFrame width="form" className="space-y-6">
      <PageHeader title="Help" />
      <p>
        <button
          type="button"
          onClick={downloadUserGuide}
          disabled={pdfBusy}
          className="text-primary hover:underline disabled:opacity-50"
        >
          {pdfBusy ? 'Preparing PDF…' : 'Download user guide (PDF)'}
        </button>
        {pdfError && (
          <span className="ml-2 text-sm text-destructive">
            Couldn't download the guide. Please try again.
          </span>
        )}
      </p>
      {[...sections.entries()].map(([section, items]) => (
        <section key={section} className="mb-8">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {section}
          </h2>
          <ul className="space-y-2">
            {items.map((d) => (
              <li key={d.slug}>
                <Link to={d.route} className="text-primary hover:underline">
                  {d.title}
                </Link>
                <p className="text-sm text-muted-foreground">{d.summary}</p>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </PageFrame>
  )
}
